import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import { TGlobalAndProductSettings } from '~types/feat/setting';

import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';

import { AdminSettingsTypeEnum, FundTypeEnum, GoldTypeDisplayMap, GoldTypeEnum, MetalTypeEnum, ModuleEnum, PermissionsEnum, SilverTypeDisplayMap } from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as Styles from './_productSettingsTable.module.scss';
import { TSilverProductFilter } from '~types/feat/silver';

type TSilverRow = TSilverProductFilter & {
    metalType: MetalTypeEnum.Silver;
};

type TTableRow = TGlobalAndProductSettings | TSilverRow;

const ProductSettingsTable = () => {
    const dispatch = useAppDispatch();

    const productListingIndex = useAppSelector(state => state.setting.settings.index);
    const silverProducts = useAppSelector(state => state.silver.allProducts);
    const { data } = useAppSelector(state => state.setting.settings);
    const access = useAppSelector(state => state.auth.userDetails.access);

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Product);

    const { Misc: { dateTimeFormat } } = UtilFunctions;

    useEffect(() => {
        dispatch(combinedActions.settingGetSettingsAttempt());
    }, [productListingIndex]);

    const handleViewProductSettingsClick = (product: TGlobalAndProductSettings) => {
        dispatch(combinedActions.settingSetProductSettingDetailsModalIsOpen(true));
        dispatch(combinedActions.settingSetProductSettingDetails(product));
    };

    const handleEditProductSettingsClick = (product: TGlobalAndProductSettings) => {
        dispatch(combinedActions.settingSetProductSettingDetailsModalIsEditing(true));
        dispatch(combinedActions.settingSetProductSettingDetailsModalIsOpen(true));
        dispatch(combinedActions.settingSetProductSettingDetails(product));
    };

    const handleDeleteProductSettingsClick = (product: TGlobalAndProductSettings) => {
        dispatch(combinedActions.settingSetProductSettingDetailsModalIsEditing(false));
        dispatch(combinedActions.settingSetProductSettingDeleteProductModalIsOpen(true));
        dispatch(combinedActions.settingSetProductSettingDetails(product));
    };

    const tableHeader = [
        'PRODUCT ID',
        'PRODUCT NAME',
        'PHYSICAL WEIGHT (GRAM)',
        'FUND TYPE',
        'METAL TYPE',
        'PURITY',
        'AFFIN BUY SPLIT',
        'TTT BUY SPLIT',
        'AFFIN SELL SPLIT',
        'TTT SELL SPLIT',
        'MINTING COST',
        'GOLD ALERT THRESHOLD',
        'CREATED AT',
        'UPDATED AT',
        'ACTION',
    ];

    const productSettings = data.filter(item => item.type === AdminSettingsTypeEnum.Product);
    const combinedProducts: TTableRow[] = [
        ...productSettings,
        ...silverProducts.map(item => ({
            ...item,
            metalType: MetalTypeEnum.Silver as const,
        })),
    ];

    const handleCalculateValue = (fixedValue?: string | null, percentageValue?: string | null) => {
        if (fixedValue) {
            const shifted = (parseFloat(fixedValue) * 100) / 100;

            return Number.isInteger(shifted * 100)
                ? `RM${shifted.toFixed(2)}`
                : `RM${parseFloat(shifted.toFixed(6)).toString()}`;
        }

        if (percentageValue) {
            const shifted = parseFloat(percentageValue) * 100;

            return Number.isInteger(shifted * 100)
                ? `${shifted.toFixed(2)}%`
                : `${parseFloat(shifted.toFixed(6)).toString()}%`;
        }

        return '-';
    };

    const tableBody = combinedProducts.map(item => {
        const isSilver = item.metalType === MetalTypeEnum.Silver;

        // settingsItem: from the settings API (has splits, timestamps, actions) — gold or silver
        // filterItem: from the silver filter API (TSilverProductFilter) — no split data
        const settingsItem = 'type' in item ? item as TGlobalAndProductSettings : undefined;

        const productId = item.productId;
        const productName = item.productName;
        const fundType = item.fundType;

        const weight = isSilver ? item.physicalSilverWeight : (settingsItem?.physicalGoldWeight);

        const getMetalTypeDisplay = () => {
            if (!settingsItem) return '-';

            if (isSilver) {
                return settingsItem.silverType
                    ? SilverTypeDisplayMap[settingsItem.silverType]
                    : 'Silver';
            }

            return settingsItem.goldType
                ? GoldTypeDisplayMap[settingsItem.goldType]
                : '-';
        };

        const getAlertDisplay = () => {
            if (!settingsItem) return '-';

            if (isSilver) {
                return settingsItem.silverType
                    ? SilverTypeDisplayMap[settingsItem.silverType]
                    : 'Silver';
            }

            return settingsItem.goldType
                ? GoldTypeDisplayMap[settingsItem.goldType]
                : '-';
        };

        const metalTypeDisplay = getMetalTypeDisplay();
        const alertDisplay = getAlertDisplay();

        return {
            className: !isSilver ? Styles['gold-row'] : '',
            cells: [
                productId,
                productName,
                weight || '-',
                fundType ? FundTypeEnum[fundType] : '-',
                metalTypeDisplay,

                settingsItem?.purity
                    ? (Math.round(Number(settingsItem.purity) * 100) / 100).toFixed(2)
                    : '-',

                settingsItem ? handleCalculateValue(settingsItem.affinBankBuySplit, settingsItem.affinBankBuySplitPercentage) : '-',
                settingsItem ? handleCalculateValue(settingsItem.tttBankBuySplit, settingsItem.tttBankBuySplitPercentage) : '-',
                settingsItem ? handleCalculateValue(settingsItem.affinBankSellSplit, settingsItem.affinBankSellSplitPercentage) : '-',
                settingsItem ? handleCalculateValue(settingsItem.tttBankSellSplit, settingsItem.tttBankSellSplitPercentage) : '-',
                settingsItem ? handleCalculateValue(settingsItem.mintingCost, settingsItem.mintingCostPercentage) : '-',

                alertDisplay,

                settingsItem?.createdAt ? dateTimeFormat(settingsItem.createdAt) : '-',
                settingsItem?.updatedAt ? dateTimeFormat(settingsItem.updatedAt) : '-',

                (
                    <div key={productId} className={Styles['action-button-container']}>
                        <ActionButton
                            label='View'
                            onClick={() => {
                                if (settingsItem) handleViewProductSettingsClick(settingsItem);
                            }}
                            className='view-btn'
                        />

                        {settingsItem && hasEditAccess && settingsItem.isEditable && (
                            <ActionButton
                                label='Edit'
                                onClick={() => handleEditProductSettingsClick(settingsItem)}
                                className='edit-btn'
                            />
                        )}

                        {settingsItem && hasEditAccess && settingsItem.isEditable && productId && productId > 11 && (
                            <ActionButton
                                label='Delete'
                                onClick={() => handleDeleteProductSettingsClick(settingsItem)}
                                className={`${Styles['delete-btn']} ${Styles['danger']}`}
                            />
                        )}
                    </div>
                ),
            ],
        };
    });

    return (
        <Table
            header={tableHeader}
            body={tableBody}
        />
    );
};

export default ProductSettingsTable;
